import { Button } from '@workspace/ui/components/button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@workspace/ui/components/input-otp'
import { TextInput } from '@workspace/ui/components/text-input'
import { maskEmail } from '@workspace/ui/lib/utils'
import { useEffect, useState } from 'react'
import { auth } from '../lib/auth'

interface LoginFormProps {
  onSuccess: (email: string) => void
}

interface VerificationCodeFormProps {
  email: string
  onBack: () => void
}

function LoginForm({ onSuccess }: LoginFormProps) {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSendOtp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const result = await auth.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      })

      if (result?.error) {
        setError(result.error.message || 'Failed to send verification code')
        return
      }

      onSuccess(email)
    } catch (err) {
      console.error('OTP send error:', err)
      setError(err instanceof Error ? err.message : 'Failed to send verification code')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSendOtp} className="w-lg flex flex-col pt-6 gap-8">
      <TextInput
        id="email"
        type="email"
        value={email}
        inputSize="lg"
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder="you@example.com"
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div>
        <Button type="submit" disabled={isLoading} size="lg" className="w-full">
          {isLoading ? 'Sending...' : 'Continue'}
        </Button>
      </div>
    </form>
  )
}

function VerificationCodeForm({ email, onBack }: VerificationCodeFormProps) {
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resendCountdown, setResendCountdown] = useState(30)
  const [canResend, setCanResend] = useState(false)

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown((prev) => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
    setCanResend(true)
  }, [resendCountdown])

  const handleVerifyOtp = async (event?: React.FormEvent<HTMLFormElement>, otpValue?: string) => {
    event?.preventDefault()

    const codeToVerify = otpValue ?? otp
    if (codeToVerify.length !== 6 || isLoading) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await auth.signIn.emailOtp({
        email,
        otp: codeToVerify,
      })

      if (result?.error) {
        setError('Invalid code. Try again or resend a new one.')
        return
      }
    } catch (err) {
      console.error('OTP verify error:', err)
      setError('Invalid code. Try again or resend a new one.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!canResend || isResending) return

    setIsResending(true)
    setError(null)
    setOtp('')

    try {
      const result = await auth.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      })

      if (result?.error) {
        setError('Failed to resend code. Please try again.')
        return
      }

      // Reset countdown
      setResendCountdown(30)
      setCanResend(false)
    } catch (err) {
      console.error('Resend error:', err)
      setError('Failed to resend code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  const handleOtpChange = (value: string) => {
    setOtp(value)
    setError(null) // Clear error when user starts typing

    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      setTimeout(() => {
        handleVerifyOtp(undefined, value)
      }, 100)
    }
  }

  return (
    <form onSubmit={handleVerifyOtp} className="w-lg flex flex-col pt-6 gap-8">
      <div className="space-y-4">
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={handleOtpChange} size="lg">
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="text-center">
          <div className="text-xs">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="text-primary hover:underline disabled:opacity-50"
              >
                {isResending ? 'Sending...' : "Didn't get it? Resend code"}
              </button>
            ) : (
              <span className="text-muted-foreground">
                Didn't get it? Resend in {resendCountdown}s
              </span>
            )}
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <div className="space-y-3">
        <Button type="submit" disabled={isLoading || otp.length !== 6} size="lg" className="w-full">
          {isLoading ? 'Verifying...' : 'Verify'}
        </Button>
        <Button type="button" variant="ghost" size="lg" onClick={onBack} className="w-full">
          Back
        </Button>
      </div>
    </form>
  )
}

export function Login() {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')

  return (
    <div className="flex items-center justify-center w-full">
      <div className="flex flex-col items-center justify-center w-full gap-2">
        <div className="flex flex-col items-center">
          <img src="/seedforge-full-light.svg" alt="SeedForge Company" className="w-64" />
          <p className="mt-2 text-lg text-white">
            {step === 'email' ? 'Enter your email' : 'Check your email'}
          </p>
          <p className="mt-1 text-sm" style={{ color: '#a8a8a8' }}>
            {step === 'email'
              ? 'Sign in to your account to continue'
              : `We sent a verification code to ${maskEmail(email)}`}
          </p>
        </div>

        {step === 'email' ? (
          <LoginForm
            onSuccess={(nextEmail) => {
              setEmail(nextEmail)
              setStep('otp')
            }}
          />
        ) : (
          <VerificationCodeForm
            email={email}
            onBack={() => {
              setStep('email')
              setEmail('')
            }}
          />
        )}
      </div>
    </div>
  )
}
